import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMundusStore } from '../../stores/mundus.store';
import { GoalTemplate, GoalType } from '../../types/mundus';

export default function MundusTemplatePicker() {
  const navigate = useNavigate();
  const { templates, fetchTemplates, createMundus } = useMundusStore();
  const [creating, setCreating] = useState<string | null>(null);

  useEffect(() => {
    if (templates.length === 0) {
      fetchTemplates();
    }
  }, []);

  const handleSelect = async (template: GoalTemplate) => {
    if (creating) return;
    setCreating(template.id);
    try {
      const mundus = await createMundus({
        name: template.name,
        description: template.description,
        goalType: (template.defaultConfig?.goalType || 'CAREER') as GoalType,
        config: { templateId: template.id, ...template.defaultConfig },
      });
      navigate(`/mundus/${mundus.id}`);
    } catch (error) {
      console.error('Failed to create mundus:', error);
    } finally {
      setCreating(null);
    }
  };

  if (templates.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
            <div className="w-10 h-10 bg-slate-100 rounded-lg mb-3" />
            <div className="h-4 bg-slate-100 rounded w-3/4 mb-2" />
            <div className="h-3 bg-slate-50 rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-900 mb-1">Choose your world</h2>
      <p className="text-sm text-slate-500 mb-4">Select a template to get started with your AI agent</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {templates.map((template) => (
          <button
            key={template.id}
            onClick={() => handleSelect(template)}
            disabled={creating !== null}
            className={`bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-blue-300 hover:shadow-md transition-all ${
              creating === template.id ? 'opacity-50 scale-95' : ''
            } disabled:cursor-wait`}
          >
            <div className="text-3xl mb-2">{template.icon}</div>
            <div className="font-semibold text-slate-900 text-sm">{template.name}</div>
            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</div>
            {template.color && (
              <div className="mt-2">
                <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600">
                  {(template.defaultConfig as any)?.category === 'business' ? 'Business' : 'Personal'}
                </span>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
